import { FirebaseFirestore } from "@capacitor-firebase/firestore";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import {
  isNativeFirestorePlatform,
  extractNativeDocuments,
} from "./firestorePlatform";

const USERS_COLLECTION = "nutzer";
const RECIPES_SUBCOLLECTION = "rezepte";

const userRecipesPath = (uid) => `${USERS_COLLECTION}/${uid}/${RECIPES_SUBCOLLECTION}`;

const normalizeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const normalizeRecipe = (id, data) => {
  const r = normalizeObject(data);

  return {
    id,
    ...r,
    title: r.title && String(r.title).trim() ? r.title : "Neues Rezept",
    content: r.content || r.text || "",
    ingredients: r.ingredients || "",
  };
};

const mapNativeRecipeDocs = (result) => {
  const docs = extractNativeDocuments(result);

  return docs.map((item, index) => {
    const id =
      item?.id ||
      item?.documentId ||
      item?.snapshot?.id ||
      item?.document?.id ||
      (typeof item?.path === "string" ? item.path.split("/").pop() : null) ||
      `recipe-${index}`;

    const data =
      item?.data ||
      item?.snapshot?.data ||
      item?.document?.data ||
      item?.value ||
      item?.fields ||
      {};

    return normalizeRecipe(id, data);
  });
};

const sortRecipesNewestFirst = (list) => {
  const toTime = (item) => {
    if (typeof item?.createdAt?.toMillis === "function") return item.createdAt.toMillis();
    if (typeof item?.updatedAt?.toMillis === "function") return item.updatedAt.toMillis();
    if (typeof item?.createdAtClient === "string") return Date.parse(item.createdAtClient) || 0;
    if (typeof item?.updatedAtClient === "string") return Date.parse(item.updatedAtClient) || 0;
    return 0;
  };

  return [...list].sort((a, b) => toTime(b) - toTime(a));
};

export const getRecipesData = async (uid) => {
  if (!uid) return [];

  if (isNativeFirestorePlatform()) {
    try {
      const result = await FirebaseFirestore.getCollection({
        reference: userRecipesPath(uid),
      });

      const recipes = mapNativeRecipeDocs(result);
      return sortRecipesNewestFirst(recipes);
    } catch (error) {
      console.error("getRecipesData native failed:", error);
      throw error;
    }
  }

  try {
    const colRef = collection(db, USERS_COLLECTION, uid, RECIPES_SUBCOLLECTION);

    let docsQuery;
    try {
      docsQuery = query(colRef, orderBy("createdAt", "desc"));
    } catch (e) {
      docsQuery = colRef;
    }

    const snapshot = await getDocs(docsQuery);
    return snapshot.docs.map((docSnap) => normalizeRecipe(docSnap.id, docSnap.data()));
  } catch (error) {
    console.error("getRecipesData web failed:", error);
    throw error;
  }
};

export const saveRecipeData = async (uid, recipeData) => {
  if (!uid) throw new Error("saveRecipeData: uid fehlt");

  const payload = {
    ingredients: recipeData?.ingredients || "",
    content: recipeData?.content || "",
    title: recipeData?.title || "",
  };

  if (isNativeFirestorePlatform()) {
    try {
      const documentId = `recipe-${Date.now()}`;

      await FirebaseFirestore.setDocument({
        reference: `${userRecipesPath(uid)}/${documentId}`,
        data: {
          ...payload,
          createdAtClient: new Date().toISOString(),
        },
      });

      return documentId;
    } catch (error) {
      console.error("saveRecipeData native failed:", error);
      throw error;
    }
  }

  try {
    const docRef = await addDoc(collection(db, USERS_COLLECTION, uid, RECIPES_SUBCOLLECTION), {
      ...payload,
      createdAt: serverTimestamp(),
    });

    return docRef.id;
  } catch (error) {
    console.error("saveRecipeData web failed:", error);
    throw error;
  }
};

export const updateRecipeData = async (uid, recipeId, data) => {
  if (!uid || !recipeId) throw new Error("updateRecipeData: uid oder recipeId fehlt");

  const payload = {
    title: data?.title || "",
    content: data?.content || "",
  };

  if (isNativeFirestorePlatform()) {
    try {
      await FirebaseFirestore.setDocument({
        reference: `${userRecipesPath(uid)}/${recipeId}`,
        data: {
          ...payload,
          updatedAtClient: new Date().toISOString(),
        },
        merge: true,
      });
      return;
    } catch (error) {
      console.error("updateRecipeData native failed:", error);
      throw error;
    }
  }

  try {
    const docRef = doc(db, USERS_COLLECTION, uid, RECIPES_SUBCOLLECTION, recipeId);
    await updateDoc(docRef, {
      ...payload,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("updateRecipeData web failed:", error);
    throw error;
  }
};

export const deleteRecipeData = async (uid, recipeId) => {
  if (!uid || !recipeId) throw new Error("deleteRecipeData: uid oder recipeId fehlt");

  if (isNativeFirestorePlatform()) {
    try {
      await FirebaseFirestore.deleteDocument({
        reference: `${userRecipesPath(uid)}/${recipeId}`,
      });
      return;
    } catch (error) {
      console.error("deleteRecipeData native failed:", error);
      throw error;
    }
  }

  try {
    const docRef = doc(db, USERS_COLLECTION, uid, RECIPES_SUBCOLLECTION, recipeId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("deleteRecipeData web failed:", error);
    throw error;
  }
};